import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import type { RelationType } from '@/services/api/schemas'
import { DetailCardHeading } from './DetailCardHeading'

export interface RelationDetailCardProps {
  relation: RelationType
  onEdit: (relation: RelationType) => void
  onDelete: (relationId: string) => void
  /** Hide the edit/delete actions when viewing a read-only (non-latest) version. */
  readOnly?: boolean
}

/** Right-panel detail view for a selected relation type. */
export function RelationDetailCard({
  relation,
  onEdit,
  onDelete,
  readOnly = false,
}: RelationDetailCardProps) {
  const { t } = useTranslation()
  return (
    <Card className="bg-card/50 border-border flex-1">
      <CardHeader className="pb-3">
        <DetailCardHeading
          key={relation.id}
          icon={<ArrowRight className="text-warning-foreground" />}
          title={relation.label}
          titleClassName="font-mono"
          actions={
            readOnly
              ? undefined
              : { onEdit: () => onEdit(relation), onDelete: () => onDelete(relation.id) }
          }
        />
        {relation.description && (
          <CardDescription className="text-sm text-muted-foreground mt-1">
            {relation.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
          {t('schemaAgent.editor.properties')}
        </div>
        {relation.properties.length > 0 ? (
          <div className="space-y-1">
            {relation.properties.map((prop, idx) => (
              <div
                key={idx}
                className="flex max-md:flex-wrap items-center justify-between max-md:gap-x-2 max-md:gap-y-1 p-2 bg-muted/30 rounded"
              >
                <span className="max-md:min-w-0 max-md:break-words text-sm text-foreground">
                  {prop.name}
                </span>
                <div className="flex max-md:ml-auto max-md:shrink-0 items-center gap-1.5">
                  <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                    {prop.type}
                  </Badge>
                  {prop.unique && (
                    <Badge
                      variant="outline"
                      className="text-xs uppercase bg-warning-surface text-warning-foreground border-warning-border"
                    >
                      {t('schemaAgent.workbench.property.unique')}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground italic py-4 text-center">
            {t('schemaAgent.editor.noProperties')}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
